import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { Spinner } from '@/components/ui/spinner';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Everything behind a session goes through here.
 *
 * The `unknown` status is why this is a component rather than a redirect: on a hard
 * refresh the browser holds a valid session cookie but the console has not yet asked
 * who it belongs to. Redirecting during that window bounces a signed-in person to the
 * sign-in screen and back, and on a slow connection they see it happen.
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const status = useAuthStore((state) => state.status);
  const restore = useAuthStore((state) => state.restore);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface-sunken">
        <Spinner className="size-6 text-brand" />
      </div>
    );
  }

  if (status === 'signedOut') {
    // The destination travels with the redirect so signing in returns the person to
    // the page they asked for, not to a dashboard they then have to navigate from.
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
};
